// The author's voice memo (design 4b "Voices"): a "Niki, aside · 0:42" pill
// that plays the memo and opens into a waveform and transcript. Used in BOTH
// the composer (editable, in the right margin) and the reader (read-only).
// It fits the 200px margin and sits inline under its paragraph on phones.
// Props are a contract: add optional ones, never remove or rename.

import { useId, useState, type KeyboardEvent } from 'react'
import type { VoiceBlock } from '../../lib/types'
import { formatDuration } from '../../lib/audio'
import { useAudioPlayer } from '../../lib/useAudioPlayer'
import { InlineConfirm, smallBtn } from './InlineConfirm'
import { PauseGlyph, PlayGlyph } from './icons'
import { Waveform } from './Waveform'

export interface VoiceMemoViewProps {
  block: VoiceBlock
  authorName: string
  /** Composer: transcript/label editing, re-record, remove. */
  editable?: boolean
  onChange?(next: VoiceBlock): void
  onRemove?(): void
  /** Composer: record a replacement (in the composer's dock). No button without it. */
  onRerecord?(): void
  /** Start expanded (reader: the first voice; composer: always). */
  defaultOpen?: boolean
}

/** Typing in the memo's fields must not reach the composer's block shortcuts (Backspace removes a selected block). */
const keepKeys = (e: KeyboardEvent) => e.stopPropagation()

export function VoiceMemoView({ block, authorName, editable = false, onChange, onRemove, onRerecord, defaultOpen }: VoiceMemoViewProps) {
  const player = useAudioPlayer(block.src, block.duration)
  const [open, setOpen] = useState(defaultOpen ?? editable)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const panelId = useId()
  const label = block.label?.trim() || 'aside'
  // Older or hand-made blocks may lack a transcript.
  const transcript = block.transcript ?? ''
  const duration = player.duration || block.duration
  const started = player.playing || player.time > 0

  // The pill plays and pauses; the first press also opens the transcript.
  const onPill = () => {
    if (!open) setOpen(true)
    player.toggle()
  }

  return (
    <div data-testid="voice-memo" className="flex w-full max-w-[420px] flex-col gap-2 font-sans">
      <button
        type="button"
        onClick={onPill}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${player.playing ? 'Pause' : 'Play'} voice memo: ${authorName}, ${label}, ${formatDuration(duration)}`}
        className="flex w-max max-w-full items-center gap-2.5 rounded-full border border-border/60 bg-card py-2 pl-2 pr-3 text-left transition-colors hover:border-border"
      >
        <span
          aria-hidden
          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"
        >
          {player.playing ? <PauseGlyph size={9} /> : <PlayGlyph size={7} />}
        </span>
        <span className="flex min-w-0 flex-col gap-px">
          <span className="truncate text-[11.5px] font-semibold text-foreground">
            {authorName}, {label}
          </span>
          <span className="text-[10.5px] font-medium tabular-nums text-muted-foreground">
            {started ? `${formatDuration(player.time)} / ${formatDuration(duration)}` : formatDuration(duration)}
          </span>
        </span>
      </button>

      {open && (
        <div id={panelId} className="flex flex-col gap-1.5 border-l-2 border-accent pl-3.5">
          <Waveform peaks={block.peaks} time={player.time} duration={duration} onSeek={player.seek} label="Seek voice memo" />
          {player.error && <span className="text-[11px] text-primary">{player.error}</span>}

          {editable ? (
            <>
              <label className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                <span className="shrink-0">{authorName},</span>
                <input
                  value={block.label ?? ''}
                  onChange={(e) => onChange?.({ ...block, label: e.target.value })}
                  onKeyDown={keepKeys}
                  placeholder="aside"
                  aria-label="Voice memo label"
                  maxLength={40}
                  className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] font-semibold text-foreground outline-none placeholder:text-muted-foreground/70 hover:border-border/60 focus:border-accent focus:bg-card"
                />
              </label>
              <textarea
                value={transcript}
                onChange={(e) => onChange?.({ ...block, transcript: e.target.value })}
                onKeyDown={keepKeys}
                rows={Math.min(8, Math.max(3, Math.ceil(transcript.length / 34) + 1))}
                aria-label="Transcript"
                placeholder="Type what you said, so readers can skim it"
                className="w-full resize-y rounded-md border border-border/60 bg-card px-2 py-1.5 font-serif text-[13.5px]/[1.45] italic text-foreground/80 outline-none placeholder:not-italic placeholder:font-sans placeholder:text-[11.5px] placeholder:text-muted-foreground/70 focus:border-accent"
              />
              <span className="text-[10.5px] font-medium text-muted-foreground">Transcript · recorded while editing</span>
              <div className="flex flex-wrap items-center gap-1 pt-0.5">
                {onRerecord && (
                  <button
                    type="button"
                    onClick={() => {
                      player.pause()
                      onRerecord()
                    }}
                    className={smallBtn.outline}
                  >
                    Re-record
                  </button>
                )}
                {onRemove && (
                  <button type="button" onClick={() => setConfirmRemove(true)} className={smallBtn.ghost}>
                    Remove
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} aria-label="Hide transcript" className={`${smallBtn.ghost} ml-auto`}>
                  Hide
                </button>
              </div>
              {confirmRemove && onRemove && (
                <InlineConfirm
                  message="Remove this voice memo?"
                  confirmLabel="Remove"
                  cancelLabel="Keep"
                  focus="cancel"
                  onConfirm={() => {
                    setConfirmRemove(false)
                    player.pause()
                    onRemove()
                  }}
                  onCancel={() => setConfirmRemove(false)}
                />
              )}
            </>
          ) : (
            <>
              {transcript.trim() && (
                <p className="m-0 font-serif text-[13.5px]/[1.45] italic text-muted-foreground">“{transcript.trim()}”</p>
              )}
              <span className="flex items-baseline justify-between gap-2 text-[10.5px] font-medium text-muted-foreground">
                <span>{transcript.trim() ? 'Transcript · recorded while editing' : 'Recorded while editing'}</span>
                <button type="button" onClick={() => setOpen(false)} aria-label="Hide transcript" className="text-muted-foreground hover:text-foreground">
                  Hide
                </button>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  )
}
